import { ErrorMessage } from '@hookform/error-message'
import { yupResolver } from '@hookform/resolvers/yup'
import { Button, Checkbox, Flex, Form, Input, Select, Skeleton, Typography, notification } from 'antd'
import { CheckboxChangeEvent } from 'antd/es/checkbox'
import dayjs from 'dayjs'
import { Fragment, useEffect, useMemo, useState } from 'react'
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { DEFAULT_VIEW_ITEMS } from 'src/app/features/feature-business-administration/user-roles/add-user-role/constants'
import EmailSwitchers from 'src/app/features/feature-business-administration/user-roles/add-user-role/EmailSwitchers'
import Expirations from 'src/app/features/feature-business-administration/user-roles/add-user-role/Expirations'
import NavigationOptions from 'src/app/features/feature-business-administration/user-roles/add-user-role/NavigationOptions'
import Switchers from 'src/app/features/feature-business-administration/user-roles/add-user-role/Switchers'
import RichTextField from 'src/app/shared/components/common/rich-text-field'
import { ActivationWarning } from 'src/app/shared/components/common/tag/ActivationWarning'
import { priGrpTypCodeName } from 'src/app/shared/constants/common.constants'
import { menuUrls } from 'src/app/shared/constants/menu.constants'
import { SERVER_STATUS_CODES } from 'src/app/shared/constants/serverResponseStatusCodes.constants'
import { SYSTEM_MESSAGE_IDS } from 'src/app/shared/constants/systemMessages.constants'
import { usePhone } from 'src/app/shared/hooks/usePhone'
import { useWindowSize } from 'src/app/shared/hooks/useWindowSize'
import { EXPIRATION_TYPE } from 'src/app/shared/models/userRole.model'
import { getAccountList, getClientById, getClientListName } from 'src/app/shared/services/ClientSetupService'
import {
  getClientDetailById,
  postAddClientUser,
} from 'src/app/shared/services/ClientUserSetupService'
import { fetchPermission, fetchSubModuleItem, fetchUserNotificationType } from 'src/app/shared/services/UserRoleService'
import { getActiveAll } from 'src/app/shared/utils'
import { getEmailsSetup, getModulesSetup, getPermissionsSetup, setUpEmails, setUpModules, setUpPermissions } from 'src/app/store/userRoleSetup'
import { ArrowDownIcon } from 'src/assets/icons'
import ConfirmModal from './ConfirmModal'
import { items } from './example'
import './index.scss'
import SelectClient from './SelectClient'
import { FormValues } from './type'
import { generateValidationSchema } from './validation'

const AddClientUserSetup = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const params = useParams()
  const { mode } = useWindowSize()
  const { id } = params

  const [loading, setLoading] = useState<boolean>(false)
  const [clientList, setClientList] = useState<any[]>([])
  const [isConfirmOpen, setIsConfirmOpen] = useState<boolean>(false)
  const [usernameConfirm, setUsernameConfirm] = useState('')
  const [accountList, setAccountList] = useState<any[]>([])
  const [isFetched, setIsFetched] = useState(false)
  const [duplicateUser, setDuplicateUser] = useState<any>(null)
  const [accountLoading, setAccountLoading] = useState<boolean>(false)
  const permissions = useSelector(getPermissionsSetup)
  const emails = useSelector(getEmailsSetup)
  const [defaultPermission, setDefaultPermission] = useState<string>('')
  const modules = useSelector(getModulesSetup)
  const [selectedClientObj, setSelectedClientObj] = useState<any>(null)
  const [dependLoading, setDependLoading] = useState<boolean>(false)
  const dispatch = useDispatch()

  const defaultValues = useMemo(() => {
    return {
      username: '',
      firstName: '',
      lastName: '',
      emailAddress: '',
      noEmailContact: false,
      primaryPhoneNumber: '',
      mobileNumber: '',
      clientName: null,
      accountNames: [],
      assignAll: false,
      hasCustomRole: true,
      navigationOptions: null,
      emails: null,
      permissions: null,
      expirations: EXPIRATION_TYPE.DOES_NOT_EXPIRE,
      expirationMinutes: null,
      expirationDays: null,
      primaryPhoneNumberRegion: '+1',
      mobileNumberRegion: '+1',
    }
  }, [])

  const getCustomUseRolesInitstate = async () => {
    setIsFetched(true)
    setDependLoading(true)
    try {
      const permissionAPI = fetchPermission({ primaryGroupTypeCodeName: priGrpTypCodeName.clientAccess })

      const [permissionRes] = await Promise.all([permissionAPI])
      if (permissionRes?.data) {
        dispatch(setUpPermissions(permissionRes?.data))
        setDefaultPermission(permissionRes?.data?.filter((item: any) => item.codeName === DEFAULT_VIEW_ITEMS)?.[0]?.id)
      }

      return {
        selectAllNavigations: false,
        selectAllPermissions: false,
        selectAllEmails: false,
        navigationOptions: [],
        permissions: [permissionRes?.data?.filter((item: any) => item.codeName === DEFAULT_VIEW_ITEMS)?.[0]?.id],
        emails: [],
        expirations: EXPIRATION_TYPE.DOES_NOT_EXPIRE,
        expirationMinutes: null,
        expirationDays: null,
      }
    } catch (error) {
      console.log('error', error)
      return {
        selectAllNavigations: false,
        selectAllPermissions: false,
        selectAllEmails: false,
        navigationOptions: [],
        permissions: [],
        emails: [],
        expirations: EXPIRATION_TYPE.DOES_NOT_EXPIRE,
        expirationMinutes: null,
        expirationDays: null,
      }
    } finally {
      setIsFetched(false)
    }
  }

  const getClientDetail = async () => {
    const defaultCustomRoleValues = await getCustomUseRolesInitstate()
    if (id) {
      try {
        const res = await getClientDetailById(id, true)
        if (res?.data) {
          setIsFetched(true)
          return {
            ...defaultValues,
            clientName: res?.data?.clientId,
            firstName: res?.data?.firstName,
            lastName: res?.data?.lastName,
            accountNames: res?.data?.accounts,
            assignAll: res?.data?.isAssignAllNewAccounts,
            hasCustomRole: true,
            noEmailContact: res?.data?.isNotReceiveOtherMail,
            primaryPhoneNumberRegion: res?.data?.primaryPrefix ?? '+1',
            mobileNumberRegion: res?.data?.mobilePrefix ?? '+1',
            primaryPhoneNumber: res?.data?.primaryPhoneNumber,
            mobileNumber: res?.data?.mobileNumber,
            selectAllNavigations: res?.data?.customRole?.navigationOptionIds?.length === getActiveAll(modules).length,
            navigationOptions: res?.data?.customRole?.navigationOptionIds ?? [],
            selectAllEmails: res?.data?.customRole?.userNotificationTypeIds?.length === emails?.length,
            emails: res?.data?.customRole?.userNotificationTypeIds?.map((item: any) => ({ emailId: item })) ?? [],
            selectAllPermissions: res?.data?.customRole?.permissionIds?.length === permissions?.length,
            permissions: res?.data?.customRole?.permissionIds ?? [],
            expirations: res?.data?.customRole?.expirationType ?? EXPIRATION_TYPE.DOES_NOT_EXPIRE,
            expirationMinutes: res?.data?.customRole?.expirationMinutes ?? null,
            expirationDays: res?.data?.customRole?.expirationDays ?? null,
          }
        }
      } catch (error) {
        console.log('error', error)
      }
    } else {
      setIsFetched(true)
    }
    return {
      ...defaultValues,
      ...defaultCustomRoleValues,
    }
  }

  const {
    control,
    formState: { errors, touchedFields },
    handleSubmit,
    setValue,
    setError,
    resetField,
    getValues,
    trigger,
    clearErrors,
  } = useForm<FormValues>({
    mode: 'all',
    defaultValues: async () => await getClientDetail(),
    resolver: yupResolver(generateValidationSchema(t) as any),
  })

  usePhone({
    ids: ['primaryPhoneNumber', 'mobileNumber'],
    setValue,
    isFetched,
    trigger,
  })

  const currentUserName = useWatch({ control, name: 'username' })
  const curentClient = useWatch({ control, name: 'clientName' })
  const { fields: emailFields, replace } = useFieldArray({ control, name: 'emails' })
  const navigationOptionsWatch = useWatch({ control, name: 'navigationOptions' })
  const permissionsWatch = useWatch({ control, name: 'permissions' })

  useEffect(() => {
    if (!curentClient) return
    setAccountLoading(true)
    getClientById({ clientId: curentClient }).then((res: any) => {
      setSelectedClientObj(res?.data ?? null)
      getAccountList(curentClient, true).then((res: any) => {
        if (res?.data) {
          setAccountList(
            res?.data?.map((item: any) => ({
              label: item.accountName,
              value: item.accountId,
            })),
          )
          setAccountLoading(false)
        }
      })
    })
  }, [curentClient])

  const isSaveButtonDisabled =
    !Object.keys(touchedFields)?.length ||
    !!Object.keys(errors)?.length ||
    !getValues('username')?.trim() ||
    !getValues('firstName')?.trim() ||
    !getValues('lastName')?.trim() ||
    !getValues('emailAddress')?.trim() ||
    !getValues('clientName') ||
    !getValues('accountNames')?.length ||
    !getValues('hasCustomRole') ||
    (getValues('hasCustomRole') && !getValues('navigationOptions')?.length)

  const handleCancel = () => {
    navigate(menuUrls.clientUserSetup)
  }

  const getCustomRolePayload = (data: FormValues) => {
    return {
      permissionIds: data?.permissions ?? [],
      userNotificationTypeIds: Array.isArray(data?.emails) ? Array.from(data?.emails).map((x) => x.emailId) : [],
      navigationOptionIds: data?.navigationOptions ?? [],
      expirationType: data?.expirations ?? EXPIRATION_TYPE.DOES_NOT_EXPIRE,
      expirationMinutes: data?.expirationMinutes ?? null,
      expirationDays: data?.expirationDays ?? null,
    }
  }

  const onSubmit = async (data: FormValues) => {
    const payload = {
      username: data?.username,
      firstName: data?.firstName,
      lastName: data?.lastName,
      email: data?.emailAddress,
      isNotReceiveOtherMail: data?.noEmailContact,
      primaryPrefix: data?.primaryPhoneNumberRegion,
      primaryPhoneNumber: data?.primaryPhoneNumber,
      mobilePrefix: data?.mobileNumberRegion,
      mobileNumber: data?.mobileNumber,
      clientId: data?.clientName,
      accounts: data?.accountNames || [],
      isAssignAllNewAccounts: data?.assignAll,
      hasCustomRole: true,
      customRole: getCustomRolePayload(data),
    }
    setLoading(true)
    postAddClientUser(payload).then((res: any) => {
      if (!res?.data?.success) {
        switch (res?.data?.code) {
          case SERVER_STATUS_CODES.DUP01_CHECK_STATUS_DUPLICATED:
          case SERVER_STATUS_CODES.DUP05_CHECK_STATUS_DUPLICATED:
            setError('username', { message: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.ALREADY_USERNAME) })
            setLoading(false)
            break
          case SERVER_STATUS_CODES.DUP02_CHECK_STATUS_DUPLICATED:
            setIsConfirmOpen(true)
            setUsernameConfirm(res?.data?.oldUser?.username)
            setDuplicateUser(res?.data?.oldUser)
            setLoading(false)
            break
          case SERVER_STATUS_CODES.DUP03_CHECK_STATUS_DUPLICATED:
          case SERVER_STATUS_CODES.DUP04_CHECK_STATUS_DUPLICATED:
          case SERVER_STATUS_CODES.DUP06_CHECK_STATUS_DUPLICATED:
            setError('emailAddress', { message: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.ALREADY_EMAIL) })
            setLoading(false)
            break
          default:
            break
        }
      } else {
        setLoading(false)
        navigate(menuUrls.clientUserSetup)
        notification.success({
          message: (
            <RichTextField
              richText={t(`SystemMessages:${SYSTEM_MESSAGE_IDS.CLIENT_USER_CREATED_SUCCESSFULLY}`, {
                Username: data.username,
              })}
            />
          ),
        })
      }
    })

    setIsConfirmOpen(false)
  }

  const handleOnchangePhoneInput = (e: any, field: any) => {
    let inputValue = e.target.value
    inputValue = inputValue.trim().replace(/\D/g, '')
    field.onChange(inputValue)
  }

  const handleOnblurPhoneInput = (e: any, field: any) => {
    let inputValue = e.target.value
    inputValue = inputValue.trim()
    field.onChange(inputValue)
  }

  const handleNavigate = () => {
    navigate(`${menuUrls.editClientUserSetup}/${duplicateUser?.id}`)
  }

  useEffect(() => {
    if (Object.keys(errors)?.length > 0) {
      setIsConfirmOpen(false)
    }
  }, [errors])

  useEffect(() => {
    setUsernameConfirm(currentUserName)
  }, [currentUserName])

  useEffect(() => {
    getClientListName(true).then((res: any) => {
      if (res?.data) {
        setClientList(
          res?.data?.map((item: any) => ({
            label: item.clientName,
            value: item.id,
            note: item.clientNotes,
          })),
        )
      }
    })
  }, [])

  useEffect(() => {
    setDefaultPermission(permissions?.filter((item: any) => item.codeName === DEFAULT_VIEW_ITEMS)?.[0]?.id)
  }, [permissions])

  useEffect(() => {
    setDependLoading(true)
    fetchSubModuleItem(true).then((res: any) => {
      if (res?.data) {
        resetField('selectAllNavigations')
        resetField('selectAllEmails')
        dispatch(setUpModules(res?.data))
        setDependLoading(false)
      }
    })

    fetchUserNotificationType('client').then((res: any) => {
      if (res?.data) {
        dispatch(setUpEmails(res?.data))
        setDependLoading(false)
      }
    })
  }, [dispatch, resetField])

  return (
    <Form name='add-client-user-form' className='form-client-user-container' layout='vertical'>
      {isFetched ? (
        <Fragment>
          {selectedClientObj?.activationDate && (
            <ActivationWarning activationDate={dayjs(selectedClientObj.activationDate)} />
          )}
          <Form.Item label={t('ClientUserSetup:username.title')} className='isRequired'>
            <Controller
              name='username'
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  status={errors?.username ? 'error' : ''}
                  placeholder={t('ClientUserSetup:username.placeholder')}
                  onChange={(e) => {
                    field.onChange(e?.target?.value.replace(/\s/g, ''))
                  }}
                  onBlur={(e) => {
                    let e1 = e
                    e1.target.value = e.target.value.trim()
                    field.onChange(e1)
                  }}
                  maxLength={50}
                />
              )}
            />
            <Typography className='error-message'>
              <ErrorMessage errors={errors} name='username' />
            </Typography>
          </Form.Item>
          <Flex
            style={{ width: '100%', flexDirection: mode === 'mobile' ? 'column' : undefined }}
            gap={mode === 'mobile' ? 0 : 24}
          >
            <Form.Item label={t('ClientUserSetup:firstName.title')} className='isRequired' style={{ width: '100%' }}>
              <Controller
                name='firstName'
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    style={{ width: '100%' }}
                    status={errors?.firstName ? 'error' : ''}
                    placeholder={t('ClientUserSetup:firstName.placeholder')}
                    onChange={(e) => {
                      field.onChange(e?.target?.value?.trimStart())
                    }}
                    onBlur={(e) => {
                      let e1 = e
                      e1.target.value = e.target.value.trim()
                      field.onChange(e1)
                    }}
                    maxLength={64}
                  />
                )}
              />
              <Typography className='error-message'>
                <ErrorMessage errors={errors} name='firstName' />
              </Typography>
            </Form.Item>

            <Form.Item label={t('ClientUserSetup:lastName.title')} className='isRequired' style={{ width: '100%' }}>
              <Controller
                name='lastName'
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    style={{ width: '100%' }}
                    status={errors?.lastName ? 'error' : ''}
                    placeholder={t('ClientUserSetup:lastName.placeholder')}
                    onChange={(e) => {
                      field.onChange(e?.target?.value?.trimStart())
                    }}
                    onBlur={(e) => {
                      let e1 = e
                      e1.target.value = e.target.value.trim()
                      field.onChange(e1)
                    }}
                    maxLength={64}
                  />
                )}
              />
              <Typography className='error-message'>
                <ErrorMessage errors={errors} name='lastName' />
              </Typography>
            </Form.Item>
          </Flex>
          <Form.Item label={t('ClientUserSetup:emailAddress.title')} className='isRequired'>
            <Controller
              name='emailAddress'
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  status={errors?.emailAddress ? 'error' : ''}
                  placeholder={t('ClientUserSetup:emailAddress.placeholder')}
                  onChange={(e) => {
                    field.onChange(e?.target?.value?.trim())
                  }}
                  maxLength={70}
                />
              )}
            />
            <Typography className='error-message'>
              <ErrorMessage errors={errors} name='emailAddress' />
            </Typography>
          </Form.Item>

          <Flex>
            <Controller
              name='noEmailContact'
              control={control}
              render={({ field }) => (
                <Checkbox
                  {...field}
                  checked={field.value}
                  onChange={(e: CheckboxChangeEvent) => {
                    setValue('noEmailContact', e.target.checked)
                  }}
                >
                  {t('ClientUserSetup:noEmailContact')}
                </Checkbox>
              )}
            />
          </Flex>

          <Flex
            style={{ width: '100%', marginTop: 24, flexDirection: mode === 'mobile' ? 'column' : undefined }}
            gap={mode === 'mobile' ? 0 : 24}
          >
            <Form.Item label={t('ClientUserSetup:primaryPhoneNumber.title')} style={{ width: '100%' }}>
              <Flex gap={8}>
                <Controller
                  name='primaryPhoneNumberRegion'
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      options={items}
                      style={{
                        width: 96,
                        height: 44,
                        borderRadius: 12,
                      }}
                      onBlur={async (event) => {
                        field.onBlur()
                      }}
                    >
                      <Typography.Text>{field?.value}</Typography.Text>
                      <ArrowDownIcon />
                    </Select>
                  )}
                />
                <Controller
                  name='primaryPhoneNumber'
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      style={{ width: '100%' }}
                      id='primaryPhoneNumber'
                      status={errors?.primaryPhoneNumber ? 'error' : ''}
                      placeholder={t('ClientUserSetup:primaryPhoneNumber.placeholder')}
                      onChange={(e) => {
                        handleOnchangePhoneInput(e, field)
                      }}
                      onBlur={(e) => {
                        handleOnblurPhoneInput(e, field)
                      }}
                      maxLength={12}
                    />
                  )}
                />
              </Flex>
              <Typography className='error-message' style={{ left: 96 }}>
                <ErrorMessage errors={errors} name='primaryPhoneNumber' />
              </Typography>
            </Form.Item>

            <Form.Item label={t('ClientUserSetup:mobileNumber.title')} style={{ width: '100%' }}>
              <Flex gap={8}>
                <Controller
                  name='mobileNumberRegion'
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      options={items}
                      style={{
                        width: 96,
                        height: 44,
                        borderRadius: 12,
                      }}
                      onBlur={async (event) => {
                        field.onBlur()
                      }}
                    >
                      <Typography.Text>{field?.value}</Typography.Text>
                      <ArrowDownIcon />
                    </Select>
                  )}
                />
                <Controller
                  name='mobileNumber'
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      style={{ width: '100%' }}
                      id='mobileNumber'
                      status={errors?.mobileNumber ? 'error' : ''}
                      placeholder={t('ClientUserSetup:mobileNumber.placeholder')}
                      onChange={(e) => {
                        handleOnchangePhoneInput(e, field)
                      }}
                      onBlur={(e) => {
                        handleOnblurPhoneInput(e, field)
                      }}
                      maxLength={12}
                    />
                  )}
                />
              </Flex>
              <Typography className='error-message' style={{ left: 96 }}>
                <ErrorMessage errors={errors} name='mobileNumber' />
              </Typography>
            </Form.Item>
          </Flex>

          <SelectClient
            control={control}
            errors={errors}
            setValue={setValue}
            clientList={clientList}
            resetField={resetField}
            accountList={accountList}
            curentClient={curentClient}
            loading={accountLoading}
            clearErrors={clearErrors}
          />

          {!dependLoading && (
            <>
              <NavigationOptions
                control={control}
                errors={errors}
                getValues={getValues}
                setValue={setValue}
                navigationOptionsWatch={navigationOptionsWatch}
                trigger={trigger}
              />
              <Switchers
                listSwitchers={permissions}
                title={t('UserRoles:permissions')}
                name='permissions'
                control={control}
                errors={errors}
                getValues={getValues}
                setValue={setValue}
                isTurnOnFirst
                data={permissionsWatch}
                resetField={resetField}
                trigger={trigger}
                allOptionName='selectAllPermissions'
                defaultPermission={defaultPermission}
              />
              <EmailSwitchers
                listSwitchers={emails}
                title={t('UserRoles:emails')}
                data={emailFields}
                replace={replace}
                control={control}
                setValue={setValue}
                trigger={trigger}
                name='emails'
              />
              <Expirations control={control} errors={errors} trigger={trigger} clearErrors={clearErrors} />
            </>
          )}

          <Flex className='button-group' justify='flex-end' gap={24}>
            <Button className='cancel-button' onClick={handleCancel}>
              {t('ClientUserSetup:cancel')}
            </Button>

            <Button
              className='save-button'
              type='primary'
              disabled={isSaveButtonDisabled}
              htmlType='button'
              onClick={handleSubmit((data) => onSubmit(data))}
              loading={loading}
              style={loading ? { width: 200 } : {}}
            >
              {t('ClientUserSetup:saveChanges')}
            </Button>
          </Flex>
          <ConfirmModal
            isOpen={isConfirmOpen}
            onClose={() => setIsConfirmOpen(false)}
            onConfirm={handleNavigate}
            Username={usernameConfirm}
          />
        </Fragment>
      ) : (
        <Skeleton active />
      )}
    </Form>
  )
}

export default AddClientUserSetup
